import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, TextField, Typography, useTheme, InputAdornment, IconButton, Tooltip, Button, CircularProgress } from "@mui/material";
import { VisibilityOutlined, VisibilityOffOutlined } from "@mui/icons-material";
import { Card } from "../../ui";
import { styled } from "../../../theme";
import useValidate from "../../../hooks/useValidate";
import { validationProfileChangePassword } from "../validations";
import { clearUpdateProfileLS, getProfile, postProfileUpdate } from "../../../reducers/Profile";
import { showNotification } from "../../../reducers/App";
import { isLoading } from "../../../constants/redux.constants";
import { getUrlParamEncoded } from "../utils/getUrlParamEncoded";



function SecurityTab() {
  document.title = "Д24 | Безопасность";
  const dispatch = useDispatch();
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();
  const token = getUrlParamEncoded('token');
  const mobileToken = decodeURIComponent(token || '');

  const { profileLS, updateProfileLS } = useSelector(state => state.profile)

  useEffect(() => {
    
    return () => {
      dispatch(clearUpdateProfileLS())
    }
  }, [])

  const [submitData, setSubmitData] = useState({ 
    old_password:       null,
    password:           null,
    password_validate:  null
  })

  const [showPassword, setShowPassword] = useState({
    old_password:         false,
    password:             false,
    password_validate:    false,
  });

  const [isPasswordMatched, setIsPasswordMatched] = useState('');

  // Валидация формы
  const { validate, errors } = useValidate({ 
    schema: validationProfileChangePassword, 
    data: {
      ...submitData, 
      old_password:       submitData.old_password         ? String(submitData.old_password).trim()        : undefined, 
      password:           submitData.password             ? String(submitData.password).trim()            : undefined, 
      password_validate:  submitData.password_validate    ? String(submitData.password_validate).trim()   : undefined,
    }
  })

  const submit = async(e) => {
    if (e) e.preventDefault();

    (submitData.password !== submitData.password_validate) ? setIsPasswordMatched('Пароли не совпадают') : setIsPasswordMatched('')
    const passwordMismatch = submitData.password !== submitData.password_validate;

    if(validate() && !passwordMismatch) {

      if(!isLoading(profileLS) && !isLoading(updateProfileLS)) {
        const result = await dispatch(postProfileUpdate(submitData));

        if(result.payload.ok) {
          dispatch(showNotification({ isOpen: true, message: 'Данные успешно обновлены', severity: 'success' }))
          setSubmitData({ old_password: null, password: null, password_validate: null })
          
          dispatch(getProfile())

          if(!mobileToken) {
            window.location.href = `https://auth.ds24.ru/?logout&message=${encodeURIComponent('Ваш пароль был изменен. Пожалуйста, зайдите с новым паролем.')}`
          }
          
        }
      }
    }

  }

  return (
    <Box>
      <Typography variant="h4" sx={{ pl: { xs: 0.5, md: 0 }, display: token ? 'none' : 'inline-flex' }} pb={3}>Безопасность</Typography>

      <Grid container xs={12} md={8} gap={{ xs: 1, md: 2 }}>
        <Card sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minWidth: '100%', 
            border: `1px solid ${theme.palette.OICO.stone[200]}`,
            p: { xs: 2, md: 3 },
            mb: { xs: 1, md: 2 },
            gap: 1
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: 14 }} pb={2}>Смена пароля</Typography>

            <form onSubmit={(e) => submit(e)}>
              <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container gap={2}>

                  <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                    <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Старый пароль *</Typography>
                    <TextField fullWidth size="small"    
                      disabled={isLoading(profileLS) || isLoading(updateProfileLS)}    
                      value={submitData.old_password ?? ''}
                      type={showPassword.old_password ? 'text' : 'password'}   
                      onChange={(e) => setSubmitData((prevData) => ({ ...prevData, old_password: e.target.value  }))}
                      sx={{ ...styled('autocomplete',"input", "filled") }}    
                      error={!!errors.old_password}
                      helperText={errors.old_password}     
                      placeholder="Старый пароль" 
                      InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: 1 }}>
                          <Tooltip placement='top' title={showPassword.old_password ? 'Скрыть пароль' : 'Показать пароль'}>
                            <IconButton
                              sx={{ opacity: submitData.old_password ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: submitData.old_password ? 'auto' : 'none' }}
                              onClick={() => setShowPassword((prev) => ({ ...prev, old_password: !prev.old_password }))}
                              edge="end"
                              size="small"
                            >
                              {showPassword.old_password ? <VisibilityOffOutlined sx={{ fill: theme.palette.grey.main }} /> : <VisibilityOutlined sx={{ fill: theme.palette.grey.main }} />}
                            </IconButton>
                          </Tooltip>
                          </InputAdornment>  
                        ),
                      }}
                    />
                  </Grid>

                  <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                    <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Новый пароль *</Typography>
                    <TextField fullWidth size="small"    
                      disabled={isLoading(profileLS) || isLoading(updateProfileLS)}      
                      value={submitData.password ?? ''}
                      type={showPassword.password ? 'text' : 'password'}  
                      error={!!errors.password || !!isPasswordMatched}
                      helperText={errors.password || isPasswordMatched}   
                      onChange={(e) => setSubmitData((prevData) => ({ ...prevData, password: e.target.value  }))}
                      sx={{ ...styled('autocomplete',"input", "filled") }}         
                      placeholder="Новый пароль" 
                      InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: 1 }}>
                          <Tooltip placement='top' title={showPassword.password ? 'Скрыть пароль' : 'Показать пароль'}>
                            <IconButton
                              sx={{ opacity: submitData.password ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: submitData.password ? 'auto' : 'none' }}
                              onClick={() => setShowPassword((prev) => ({ ...prev, password: !prev.password }))}
                              edge="end"
                              size="small"
                            >
                              {showPassword.password ? <VisibilityOffOutlined sx={{ fill: theme.palette.grey.main }} /> : <VisibilityOutlined sx={{ fill: theme.palette.grey.main }} />}
                            </IconButton>
                          </Tooltip>
                          </InputAdornment>  
                        ),
                      }}
                    />
                  </Grid>

                  <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                    <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Повторите новый пароль *</Typography>
                    <TextField fullWidth size="small"    
                      disabled={isLoading(profileLS) || isLoading(updateProfileLS)}      
                      value={submitData.password_validate ?? ''}
                      error={!!errors.password_validate || !!isPasswordMatched}
                      helperText={errors.password_validate || isPasswordMatched}   
                      type={showPassword.password_validate ? 'text' : 'password'}   
                      onChange={(e) => setSubmitData((prevData) => ({ ...prevData, password_validate: e.target.value  }))}
                      sx={{ ...styled('autocomplete',"input", "filled") }}         
                      placeholder="Повторите новый пароль" 
                      InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: 1 }}>
                          <Tooltip placement='top' title={showPassword.password_validate ? 'Скрыть пароль' : 'Показать пароль'}>
                            <IconButton
                              sx={{ opacity: submitData.password_validate ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: submitData.password_validate ? 'auto' : 'none' }}
                              onClick={() => setShowPassword((prev) => ({ ...prev, password_validate: !prev.password_validate }))}
                              edge="end"
                              size="small"
                            >
                              {showPassword.password_validate ? <VisibilityOffOutlined sx={{ fill: theme.palette.grey.main }} /> : <VisibilityOutlined sx={{ fill: theme.palette.grey.main }} />}
                            </IconButton>
                          </Tooltip>
                          </InputAdornment>  
                        ),
                      }}
                    />
                  </Grid>

                  <Grid xs={12} item display='flex' alignItems='center' justifyContent='space-between' gap={1.5} pt={2}>
                    <Button 
                      disabled={((isLoading(profileLS) || isLoading(updateProfileLS)) || !(submitData.old_password && submitData.password && submitData.password_validate))} 
                      sx={{ textTransform: 'none', paddingX: '30px', paddingY: '12px', width: { xs: '100%', md: 'auto' } }} 
                      disableElevation 
                      variant="contained" 
                      color={"green"} 
                      type="submit"
  
                    >
                      {isLoading(updateProfileLS) && (<Box  sx={{ alignItems: 'center', display: 'flex' }} pr={1}><CircularProgress size={15} color='inherit' /></Box>)}
                      Сохранить
                    </Button>
                  </Grid>

                </Grid>
              </Grid>
            </form>
          </Card>
      </Grid>
    </Box>
  )
}

export default SecurityTab;